import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { Formik, type FormikProps } from 'formik';
import type { FC } from 'react';
import { useCallback } from 'react';

import { DISPATCH_SYSTEM_NAMES } from '@core/constants/integrations';
import {
  type IntegrationFormikType,
  IntegrationSchema,
  formikIntegrationToWire,
  integrationFormik,
} from '@core/formik/integration';
import Enums from '@core/types/api/Enums';
import type { Integration } from '@core/types/api/Integration/Integration';
import { hasValue } from '@core/utils/equality';
import { FormErrorNotification, SetFormikValue } from '@core/utils/formikHelpers';
import { NIL_UUID } from '@core/utils/uuid';
import { Input } from '@web/components/Input/Input';
import { JsonConfigEditor } from '@web/components/JsonConfigEditor';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '@web/components/SlabDrawer/SlabDrawer';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { QueryError } from '@web/utils/Query';

interface IntegrationDrawerProps {
  isOpen: boolean;
  onDismiss(): void;
  integration: Integration | null;
  tenantIdOverride: string;
}

export const IntegrationDrawer: FC<IntegrationDrawerProps> = ({
  isOpen,
  onDismiss,
  integration,
  tenantIdOverride,
}) => {
  const createIntegration = useSlabMutation('POST integration as admin');
  const updateIntegration = useSlabMutation('PUT integration by ID as admin');
  const archiveIntegration = useSlabMutation('POST integration archive as admin');

  const isEditMode = hasValue(integration);

  const toast = useToast({ subjectName: 'integration' });
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const handleSuccess = useCallback(() => {
    toast.success();
    onDismiss();
  }, [toast, onDismiss]);

  const handleError = useCallback(
    (error: QueryError) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
    [toast],
  );

  const handleArchive = useCallback(() => {
    if (integration === null) return;

    archiveIntegration.mutate(
      {
        args: {
          pathParams: { tenantID: tenantIdOverride, id: integration.id },
          body: {},
        },
      },
      {
        onSuccess: handleSuccess,
        onError: handleError,
      },
    );
  }, [integration, archiveIntegration, tenantIdOverride, handleSuccess, handleError]);

  const formikIntegration = integrationFormik(integration);

  const integrationTypeOptions = Object.values(Enums.IntegrationType).map((integrationType) => ({
    value: integrationType,
    label: DISPATCH_SYSTEM_NAMES[integrationType],
  }));

  const requestClose = (formikBag: FormikProps<IntegrationFormikType>): void =>
    guardClose({
      isDirty: integration?.isArchived !== true && editTracker.isEdited(),
      canSave: formikBag.isValid,
      isSaving: createIntegration.isPending || updateIntegration.isPending,
      onSave: formikBag.submitForm,
      onDiscard: onDismiss,
    });

  return (
    <SlabDrawer isOpen={isOpen} close={onDismiss}>
      <Formik
        validationSchema={IntegrationSchema}
        initialValues={formikIntegration}
        onSubmit={async (values: IntegrationFormikType, { setSubmitting }): Promise<void> => {
          const wireIntegration = formikIntegrationToWire(values);

          const mutationOptions = {
            onSuccess: (): void => {
              setSubmitting(false);
              handleSuccess();
            },
            onError: (error: QueryError): void => {
              setSubmitting(false);
              handleError(error);
            },
          };

          if (formikIntegration.id === NIL_UUID) {
            createIntegration.mutate(
              {
                args: {
                  body: wireIntegration,
                  pathParams: { tenantID: tenantIdOverride },
                },
                // Don't validate with schema here - already validated by Formik
                // and wireIntegration has config as object, not string
              },
              mutationOptions,
            );
          } else {
            updateIntegration.mutate(
              {
                args: {
                  pathParams: {
                    integrationID: formikIntegration.id,
                    tenantID: tenantIdOverride,
                  },
                  body: wireIntegration,
                },
                // Don't validate with schema here - already validated by Formik
                // and wireIntegration has config as object, not string
              },
              mutationOptions,
            );
          }
        }}
      >
        {(formikBag) => (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={() => requestClose(formikBag)} editTracker={editTracker} />
            <Box display='flex' flexDirection='column' gap={3}>
              <IconButton sx={{ alignSelf: 'flex-start' }} onClick={() => requestClose(formikBag)}>
                <Close />
              </IconButton>

              <Typography variant='h1'>
                {isEditMode ? 'Edit integration' : 'Add new integration'}
              </Typography>

              <FormErrorNotification />

              <Box display='flex' flexDirection='column' gap={2}>
                <Input name='name' label='Name*' placeholder='e.g., Allied Production' />

                <FormControl fullWidth error={hasValue(formikBag.errors.integrationType)}>
                  <InputLabel id='integration-type-label'>Integration Type*</InputLabel>
                  <Select
                    labelId='integration-type-label'
                    value={formikBag.values.integrationType}
                    label='Integration Type*'
                    onChange={(event) => {
                      SetFormikValue(formikBag, 'integrationType', event.target.value);
                    }}
                  >
                    {integrationTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formikBag.errors.integrationType !== null &&
                    formikBag.errors.integrationType !== undefined &&
                    formikBag.errors.integrationType !== '' && (
                      <FormHelperText>{formikBag.errors.integrationType}</FormHelperText>
                    )}
                </FormControl>

                <Input
                  name='externalIdentifier'
                  label='External Identifier*'
                  placeholder='e.g., allied-prod-001'
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formikBag.values.isActive}
                      onChange={(event) => {
                        SetFormikValue(formikBag, 'isActive', event.target.checked);
                      }}
                    />
                  }
                  label='Active'
                />

                <JsonConfigEditor
                  value={formikBag.values.config}
                  onChange={(value) => SetFormikValue(formikBag, 'config', value)}
                  error={hasValue(formikBag.errors.config) && formikBag.touched.config}
                  helperText={
                    formikBag.touched.config === true &&
                    formikBag.errors.config !== null &&
                    formikBag.errors.config !== undefined &&
                    formikBag.errors.config !== ''
                      ? String(formikBag.errors.config)
                      : undefined
                  }
                />
              </Box>
            </Box>

            <Box display='flex' gap={2} justifyContent='space-between'>
              <Box>
                {isEditMode && !integration?.isArchived && (
                  <Button variant='outlined' color='error' onClick={handleArchive}>
                    {integration?.isArchived ? 'Unarchive' : 'Archive'}
                  </Button>
                )}
              </Box>
              <Box display='flex' gap={2}>
                <Button variant='outlined' onClick={() => requestClose(formikBag)}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={() => formikBag.handleSubmit()}
                  disabled={formikBag.isSubmitting}
                >
                  {isEditMode ? 'Save changes' : 'Create integration'}
                </Button>
              </Box>
            </Box>
          </SlabDrawerLayout>
        )}
      </Formik>
    </SlabDrawer>
  );
};
